import React, { useState, useEffect, useMemo } from 'react';
import { Table, Input, Alert, Statistic, Row, Col, Card, Tag, Tabs, message, Button } from 'antd';
import { DownloadOutlined, WarningOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { getSchoolsReport } from '../../api/reports';
import { numberSorter, textSorter } from '../../utils/tableSorters';
import { SCHOOL_TYPE_LABELS } from '../common/SchoolSelect';

/**
 * Okul bazlı öğrenci dağılımı.
 *
 * İki sekme, iki ayrı soru:
 *  - Okullar: kataloğa bağlı öğrenciler. "Hangi okuldan kaç öğrencimiz var"
 *    sorusunun güvenilir cevabı — okul artık id ile tutuluyor, yazım farkı
 *    diye bir şey yok.
 *  - Bağlanmamış: kataloğa oturtulamamış ham metinler. Temizliğin kalan iş
 *    listesi; boşalması hedefleniyor.
 *
 * Liste tek seferde geliyor (okul sayısı küçük), bu yüzden istemci
 * sıralaması doğru sonuç veriyor — server-side sıralama gerekmiyor.
 */
const SchoolsReportTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    getSchoolsReport()
      .then(res => setData(res.data))
      .catch(() => message.error('Okul raporu yüklenemedi.'))
      .finally(() => setLoading(false));
  }, []);

  const rows = data?.rows || [];
  const unlinked = data?.unlinked || [];
  const summary = data?.summary || {};

  const filtre = (liste) => {
    const q = search.trim().toLocaleLowerCase('tr');
    if (!q) return liste;
    return liste.filter(r => (r.school || '').toLocaleLowerCase('tr').includes(q));
  };

  const filtreliOkullar    = useMemo(() => filtre(rows),     [rows, search]);     // eslint-disable-line react-hooks/exhaustive-deps
  const filtreliBaglanmamis = useMemo(() => filtre(unlinked), [unlinked, search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Temizlik yapılırken kullanılacak ham liste — panoya kopyalanıp bir
  // tabloya yapıştırılabilsin diye (sekmeyle ayrılmış).
  const kopyala = (liste, baslik) => {
    const metin = liste
      .filter(r => r.school)
      .map(r => `${r.school}\t${r.active_count}\t${r.passive_count}\t${r.candidate_count}\t${r.total_count}`)
      .join('\n');
    navigator.clipboard.writeText(`${baslik}\tAktif\tPasif\tAday\tToplam\n${metin}`)
      .then(() => message.success('Liste panoya kopyalandı.'))
      .catch(() => message.error('Kopyalanamadı.'));
  };

  const sayiKolonlari = [
    {
      title: 'Aktif', dataIndex: 'active_count', align: 'center', width: 90,
      sorter: numberSorter('active_count'),
      render: v => <span className="text-green-600 font-medium">{v}</span>,
    },
    {
      title: 'Pasif', dataIndex: 'passive_count', align: 'center', width: 90,
      sorter: numberSorter('passive_count'),
      render: v => <span className="text-gray-500">{v}</span>,
    },
    {
      title: 'Aday', dataIndex: 'candidate_count', align: 'center', width: 90,
      sorter: numberSorter('candidate_count'),
      render: v => <span className="text-orange-500">{v}</span>,
    },
    {
      title: 'Toplam', dataIndex: 'total_count', align: 'center', width: 100,
      sorter: numberSorter('total_count'),
      defaultSortOrder: 'descend',
      render: v => <strong>{v}</strong>,
    },
  ];

  const okulKolonlari = [
    {
      title: 'Okul',
      dataIndex: 'school',
      sorter: textSorter('school'),
      render: (v, r) => (
        <span style={{ opacity: r.is_active ? 1 : 0.5 }}>
          {v}
          {!r.is_active && <Tag style={{ marginLeft: 8 }}>Pasif</Tag>}
        </span>
      ),
    },
    {
      title: 'Tür', dataIndex: 'type', width: 110,
      sorter: textSorter('type'),
      render: v => <Tag>{SCHOOL_TYPE_LABELS[v] || v}</Tag>,
    },
    {
      title: 'Sahiplik', dataIndex: 'ownership', width: 100,
      sorter: textSorter('ownership'),
      render: v => (v === 'ozel' ? <Tag color="gold">Özel</Tag> : <Tag>Resmî</Tag>),
    },
    { title: 'İlçe', dataIndex: 'district', width: 110, sorter: textSorter('district') },
    ...sayiKolonlari,
    {
      title: 'Doğrulanacak',
      dataIndex: 'review_count',
      align: 'center',
      width: 130,
      sorter: numberSorter('review_count'),
      // Taşımada YAŞ TAHMİNİYLE bağlanan kayıtlar. Tahmin genelde doğru ama
      // eski bir kayıtta öğrenci o günkü kademesinden ilerlemiş olabilir.
      render: v => (v > 0
        ? <Tag color="orange" icon={<WarningOutlined />}>{v}</Tag>
        : <span style={{ color: '#ccc' }}>—</span>),
    },
  ];

  const baglanmamisKolonlari = [
    {
      title: 'Girilen metin',
      dataIndex: 'school',
      sorter: textSorter('school'),
      // whiteSpace: 'pre' ŞART — HTML ardışık boşlukları birleştirir, bu
      // yüzden "Aliağa  Doğa" (çift boşluk) ile "Aliağa Doğa" ekranda birebir
      // aynı görünüyordu; baştaki/sondaki boşluklar da görünmez oluyordu.
      render: (v) => v
        ? <span style={{ fontFamily: 'monospace', whiteSpace: 'pre' }}>{v}</span>
        : <Tag>Girilmemiş</Tag>,
    },
    ...sayiKolonlari,
  ];

  return (
    <div>
      <Row gutter={16} className="mb-4">
        <Col span={6}>
          <Card size="small">
            <Statistic title="Kayıtlı okul" value={summary.school_count ?? 0} loading={loading} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="Okula bağlı öğrenci" value={summary.linked_students ?? 0} loading={loading} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Doğrulanacak"
              value={summary.review_students ?? 0}
              loading={loading}
              // valueStyle DEĞİL: AntD v6'da deprecated, konsola uyarı basıyor.
              styles={{ content: { color: (summary.review_students ?? 0) > 0 ? '#fa8c16' : undefined } }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Bağlanmamış öğrenci"
              value={summary.unlinked_students ?? 0}
              loading={loading}
              styles={{ content: { color: (summary.unlinked_students ?? 0) > 0 ? '#cf1322' : undefined } }}
            />
          </Card>
        </Col>
      </Row>

      <div className="flex justify-between items-center mb-3 gap-3">
        <Input
          placeholder="Okul adında ara..."
          allowClear
          style={{ width: 280 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <Tabs
        items={[
          {
            key: 'schools',
            label: `Okullar (${rows.length})`,
            children: (
              <>
                <div className="flex justify-end mb-2">
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={() => kopyala(rows, 'Okul')}
                    disabled={rows.length === 0}
                  >
                    Listeyi Kopyala
                  </Button>
                </div>
                <Table
                  rowKey="school_id"
                  columns={okulKolonlari}
                  dataSource={filtreliOkullar}
                  loading={loading}
                  size="small"
                  pagination={{
                    pageSize: 50, showSizeChanger: true,
                    pageSizeOptions: ['25', '50', '100', '200'],
                    showTotal: (t) => `${t} okul`,
                  }}
                />
              </>
            ),
          },
          {
            key: 'unlinked',
            label: (
              <span>
                Bağlanmamış ({unlinked.length})
                {summary.unlinked_students > 0 && (
                  <Tag color="red" style={{ marginLeft: 8 }}>{summary.unlinked_students}</Tag>
                )}
              </span>
            ),
            children: (
              <>
                <Alert
                  type={unlinked.length ? 'warning' : 'success'}
                  showIcon
                  className="mb-3"
                  message={unlinked.length
                    ? 'Bu kayıtlar okul listesine bağlanamadı'
                    : 'Tüm öğrenciler okul listesine bağlı'}
                  description={unlinked.length ? (
                    <>
                      Aliağa dışındaki okullar, yazım hataları ve "İlkokul" gibi hangi okul
                      olduğu anlaşılamayan girişler burada. Düzeltmek için öğrencinin kartını
                      açıp <strong>Okul</strong> alanından doğru okulu seçin; okul listede yoksa
                      önce <Link to="/schools">Okullar</Link> sayfasından ekleyin.
                      Metinler <strong>girildiği gibi</strong> gösteriliyor — boşluk ve
                      büyük/küçük harf farkları bilerek korunuyor.
                    </>
                  ) : undefined}
                />
                <div className="flex justify-end mb-2">
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={() => kopyala(unlinked, 'Girilen metin')}
                    disabled={unlinked.length === 0}
                  >
                    Listeyi Kopyala
                  </Button>
                </div>
                <Table
                  rowKey={(r) => r.school ?? '__bos__'}
                  columns={baglanmamisKolonlari}
                  dataSource={filtreliBaglanmamis}
                  loading={loading}
                  size="small"
                  pagination={{
                    pageSize: 50, showSizeChanger: true,
                    pageSizeOptions: ['25', '50', '100', '200'],
                    showTotal: (t) => `${t} farklı yazım`,
                  }}
                />
              </>
            ),
          },
        ]}
      />
    </div>
  );
};

export default SchoolsReportTab;
